import { Injectable } from '@angular/core';
import { Feature } from 'geojson';
import { PopulationSamples, Sex } from '../../../../../shared/model/population';
import { mapAgeSexDistribution } from '../../../../../shared/model/population-mapper';
import { range } from '../../../../../shared/util/arrays';
import { AgeRange } from '../../../../../shared/model/range';
import { MapHttpService } from '../../../../mapbox/services/map-http.service';
import { JsonResponse } from '../../../../../shared/api/backend-config';
import { catchError, forkJoin, of } from 'rxjs';
import {
  formatChartRangesAndHoverNumber,
  formatNumber,
  pieChartMargins,
  setAccessibleYears,
  withLoading,
} from '../shared/chart-utility';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { Data, Layout } from 'plotly.js-dist-min';
import { convertLayerIdToCellType } from '../../../../../shared/util/layerIdToCellType';
import { POPULATION_BY_YEARS_CHART } from '../../../../../shared/types/feature-data-type';

export interface RacePopulationByYear {
  racePopulationByYear: {
    year: number;
    racePopulation: {
      [key: string]: number;
    };
  }[];
}

export interface AgeSexDataJSON {
  year: number;
  sex: Sex;
  ageFrom: number;
  ageTo: number;
  population: number;
}

export const MARITAL_STATUS = 'MARITAL_STATUS';

export interface MaritalStatusJSON {
  MARITAL_STATUS: MaritalStatus[];
}

export interface MaritalStatus {
  year: number;
  married: number;
  notMarried: number;
}

export interface ChartDataByYear {
  [year: number]: {
    data: Data;
    layout: Layout;
  };
}

export interface XAxisData {
  title: string;
  range: number[];
  tickvals: number[];
  ticktext: (string | number)[];
  tickangle: number;
}

export interface FeatureStatus {
  featureId?: Feature['id'];
  naturalId?: NaturalId;
  isSelected: boolean;
  /**
   * A request for this group's charts is in flight. Maintained by
   * `withLoading`, read by the chart blocks so that none of them describes a
   * half-filled status as if it were the final answer.
   */
  isLoading?: boolean;
}

export interface PopulationFeatureStatus extends FeatureStatus {
  containsAgeSexInfo: boolean;
  ageSexYear: number;
  containsRaceDistribution: boolean;
  containsPopulationByYears: boolean;
  raceDistributionYear: number;
  containsMaritalStatus: boolean;
  maritalStatusYear: number;
}

export const MALE_COLOR = '#01a5ff';
export const FEMALE_COLOR = '#8247ff';
export const ALL_COLOR = '#02A4FF';
const POPULATION_TREND_START_YEAR = 2010;

export type NaturalId = string;

@Injectable({
  providedIn: 'root',
})
export class PopulationService {
  private readonly raceKeyToReadableName = new Map([
    ['OTHER', 'Some other race alone'],
    ['WHITE', 'White'],
    ['AMERICAN_INDIAN_AND_ALASKA', 'American Indian or Alaska Native'],
    ['TWO_OR_MORE', 'Two or more races'],
    ['BLACK', 'Black or African American'],
    ['ASIAN', 'Asian'],
    [
      'HAWAIIAN_AND_PACIFIC_ISLANDER',
      'Native Hawaiian or Other Pacific Islander',
    ],
  ]);

  private readonly colorsPool = [
    '#3B75AF',
    '#EF8636',
    '#519E3E',
    '#C53A32',
    '#8D69B8',
    '#EFB743',
    '#9CE0FC',
  ];

  private cellMaritalStatusData: MaritalStatus[] = [];
  private usaMaritalStatusData: MaritalStatus[] = [];

  public readonly maritalStatusYears: number[] = [];

  public readonly ageSexAccessibleYears: number[] = [];

  public readonly raceDistributionAccessibleYears: number[] = [];

  public readonly featureStatus: PopulationFeatureStatus = {
    featureId: undefined,
    isSelected: false,
    containsAgeSexInfo: false,
    containsPopulationByYears: false,
    ageSexYear: 0,
    containsRaceDistribution: false,
    raceDistributionYear: 0,
    containsMaritalStatus: false,
    maritalStatusYear: 0,
  };

  public populationTrendChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly maritalStatusChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public readonly ageSexChartDataByYear: ChartDataByYear = {};

  public readonly raceDistributionDataByYear: ChartDataByYear = {};

  private readonly layoutLegend = {
    x: -0.15,
    y: 1.2,
    orientation: 'h',
  };

  private static readonly ageRangesPyramidAggregation: AgeRange[] = [
    new AgeRange(0, 6),
    ...range(5, 85, 5).map((i) => new AgeRange(i, i + 5)),
    new AgeRange(85, AgeRange.MAX_AGE),
  ];

  constructor(private http: MapHttpService) {}

  public fillChartsData(feature: MapboxGeoJSONFeature): void {
    const naturalId = feature.properties!.external_id;

    this.featureStatus.naturalId = naturalId;
    this.featureStatus.isSelected = true;

    this.http
      .getAgeSexData(naturalId)
      .pipe(withLoading(this.featureStatus))
      .subscribe(
        (data: JsonResponse<AgeSexDataJSON[]>) => {
          if (data.object) {
            const ageSexDistribution: PopulationSamples = mapAgeSexDistribution(
              data.object
            );

            this.ageSexAccessibleYears.length = 0;

            this.fillAgeSexPyramidData(ageSexDistribution, naturalId);
          } else {
            this.featureStatus.containsAgeSexInfo = false;
            return;
          }
        },
        (error: any) => {
          this.featureStatus.containsAgeSexInfo = false;
          console.error('ERROR FETCHING AGE SEX', error);
          return of(null);
        }
      );

    this.http
      .getDataForSingleCell(
        naturalId,
        convertLayerIdToCellType(feature.layer.id)!,
        [POPULATION_BY_YEARS_CHART]
      )
      .pipe(withLoading(this.featureStatus))
      .subscribe(
        (data: JsonResponse<any>) => {
          if (data.object && data.object[POPULATION_BY_YEARS_CHART]) {
            this.fillPopulationTrendData(
              data.object[POPULATION_BY_YEARS_CHART]
            );
          } else {
            this.featureStatus.containsPopulationByYears = false;
            return;
          }
        },
        (error: any) => {
          this.featureStatus.containsPopulationByYears = false;
          console.error('ERROR FETCHING POPULATION BY YEARS', error);
          return of(null);
        }
      );

    this.fillRaceDistributionChart(naturalId);

    forkJoin({
      cell: this.http.getDataForSingleCellUnified<MaritalStatusJSON>(
        naturalId,
        [MARITAL_STATUS]
      ),
      usa: this.http.getDataForSingleCellUnified<MaritalStatusJSON>(
        'US',
        [MARITAL_STATUS],
        { country: true }
      ),
    })
      .pipe(withLoading(this.featureStatus))
      .subscribe(({ cell, usa }) => {
        if (cell.object && usa.object) {
          this.featureStatus.containsMaritalStatus = true;
          this.featureStatus.isSelected = true;
          this.featureStatus.naturalId = naturalId;

          this.cellMaritalStatusData = cell.object.MARITAL_STATUS;
          this.usaMaritalStatusData = usa.object.MARITAL_STATUS;

          // The chart needs both series, so only years present in both are selectable.
          const usaYears = new Set(
            this.usaMaritalStatusData.map((d) => d.year)
          );
          setAccessibleYears(
            this.maritalStatusYears,
            this.cellMaritalStatusData.filter((d) => usaYears.has(d.year))
          );

          this.featureStatus.maritalStatusYear = Math.max(
            ...this.maritalStatusYears
          );

          this.updateMaritalStatusChartByYear(
            this.featureStatus.maritalStatusYear
          );
        } else {
          setAccessibleYears(this.maritalStatusYears, []);
          this.featureStatus.containsMaritalStatus = false;
        }
      });
  }

  public updateMaritalStatusChartByYear(year: number): void {
    if (!this.cellMaritalStatusData || !this.usaMaritalStatusData) {
      return;
    }

    const cellEntry = this.cellMaritalStatusData.find((d) => d.year === year);
    const usaEntry = this.usaMaritalStatusData.find((d) => d.year === year);

    if (!cellEntry || !usaEntry) {
      this.featureStatus.containsMaritalStatus = false;
      return;
    }

    this.featureStatus.maritalStatusYear = year;

    this.fillMaritalStatusChart(
      { MARITAL_STATUS: [cellEntry] },
      { MARITAL_STATUS: [usaEntry] }
    );
  }

  private fillMaritalStatusChart(
    cellData: MaritalStatusJSON,
    usaData: MaritalStatusJSON
  ): void {
    const latestYear = this.featureStatus.maritalStatusYear;

    const cellEntry = cellData.MARITAL_STATUS.find(
      (d) => d.year === latestYear
    );
    const usaEntry = usaData.MARITAL_STATUS.find((d) => d.year === latestYear);

    if (!cellEntry || !usaEntry) {
      this.featureStatus.containsMaritalStatus = false;
      return;
    }

    const categories = ['Married', 'Not Married'];
    const cellValues = [cellEntry.married, cellEntry.notMarried];
    const usaValues = [usaEntry.married, usaEntry.notMarried];

    const totalCell = cellValues.reduce((sum, val) => sum + val, 0);
    const totalUSA = usaValues.reduce((sum, val) => sum + val, 0);

    const cellPercents = cellValues.map((v) => (v / totalCell) * 100);
    const usaPercents = usaValues.map((v) => (v / totalUSA) * 100);

    if (totalCell < 10) {
      this.featureStatus.containsMaritalStatus = false;
      return;
    }

    this.featureStatus.containsMaritalStatus = true;

    this.maritalStatusChart.data = [
      {
        name: 'Cell',
        x: categories,
        y: cellPercents,
        type: 'bar',
        customdata: cellPercents.map((p) => `${p.toFixed(1)}%`),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: {
          color: categories.map((value) => {
            switch (value) {
              case 'Married':
                return '#A084BD';
              case 'Not Married':
                return '#8561AA';
              default:
                return '#CCCCCC';
            }
          }),
        },
      },
      {
        name: 'National',
        x: categories,
        y: usaPercents,
        type: 'bar',
        opacity: 0.5,
        customdata: usaPercents.map((p) => `${p.toFixed(1)}%`),
        hovertemplate: `%{customdata}<extra></extra>`,
        marker: {
          color: categories.map((value) => {
            switch (value) {
              case 'Married':
                return '#A084BD';
              case 'Not Married':
                return '#8561AA';
              default:
                return '#CCCCCC';
            }
          }),
        },
      },
    ] as Data;

    this.maritalStatusChart.layout = {
      autosize: true,
      legend: {
        x: -0.15,
        y: 1.2,
        orientation: 'h',
      },
      xaxis: {
        title: {
          text: 'Marital Status',
          font: {
            color: '#fff',
            size: 1,
          },
        },
        tickangle: 0,
        tickvals: categories,
        ticktext: categories,
        tickfont: {
          size: 10,
        },
      },
      yaxis: {
        title: 'Population (%)',
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      bargap: 0.4,
      barmode: 'group',
      margin: { t: 35, r: 30 },
      font: {
        family: 'Manrope, sans-serif',
      },
    } as Layout;
  }

  private fillRaceDistributionChart(id: NaturalId): void {
    this.http
      .getRaceDistributionData(id)
      .pipe(
        catchError((err) => {
          this.featureStatus.containsRaceDistribution = false;
          console.error('ERROR FETCHING RACE_DISTRIBUTION', err);
          return of(null);
        }),
        withLoading(this.featureStatus)
      )
      .subscribe((data) => {
        if (!data) return;

        this.raceDistributionAccessibleYears.length = 0;

        if (data?.object) {
          data.object.racePopulationByYear.forEach((el) => {
            const values: number[] = Object.values(el.racePopulation);
            const labels: string[] = Object.keys(el.racePopulation).map(
              (el) => {
                return this.raceKeyToReadableName.get(el)!;
              }
            );

            this.raceDistributionAccessibleYears.push(el.year);

            const maxValue = Math.max(...values);

            if (!maxValue || maxValue === -Infinity) {
              this.featureStatus.naturalId = id;
              this.featureStatus.containsRaceDistribution = false;
            } else {
              this.featureStatus.naturalId = id;
              this.featureStatus.containsRaceDistribution = true;
            }

            const layout = {
              autosize: true,
              font: {
                family: 'Manrope, sans-serif',
              },
              height: 500,
              width: 450,

              margin: pieChartMargins,
              legend: {
                orientation: 'h',
                width: '100%',
                y: 1.6,
                x: 0.03,
              },
              marker: {
                colors: this.colorsPool,
              },
            };

            this.raceDistributionDataByYear[el.year] = {
              data: [
                {
                  values: values,
                  labels: labels,
                  type: 'pie',
                  textposition: 'inside',
                  hoverinfo: 'label+percent+value',
                  hovertemplate:
                    '&nbsp;&nbsp;%{label}: %{customdata} (%{percent})&nbsp;&nbsp;<extra></extra>',
                  customdata: values.map((val) => formatNumber(val)),
                },
              ] as Data,
              layout: layout as any,
            };
          });

          this.raceDistributionAccessibleYears.sort((a, b) => a - b);
          this.featureStatus.raceDistributionYear = Math.max(
            ...this.raceDistributionAccessibleYears
          );
        }
      });
  }

  private fillPopulationTrendData(byYears: { [key: string]: number }) {
    const filteredEntries = Object.entries(byYears).filter(
      ([year, _]) => parseInt(year) >= POPULATION_TREND_START_YEAR
    );
    const years = filteredEntries.map(([year, _]) => year);
    const values = filteredEntries.map(([_, value]) => value);

    const maxValue = Math.max(...values);
    const bottomValue = Math.min(...values) * 0.95;

    this.featureStatus.containsPopulationByYears = !values.some(
      (el) => el <= 10
    );

    const barWidth = 0.5;

    const bothSexData = {
      x: years,
      y: values,
      name: 'All',
      type: 'bar',
      customdata: values.map(
        (population) => `${formatChartRangesAndHoverNumber(population)}`
      ),
      hovertemplate: `%{customdata}<extra></extra>`,
      marker: { color: ALL_COLOR },
      width: barWidth,
    };

    const populationTrendData = [bothSexData];

    const populationTrendLayout = {
      autosize: true,
      margin: {
        t: 30,
        r: 35,
      },
      xaxis: {
        title: 'Year',
        type: 'category',
      },
      yaxis: {
        title: 'Population',
        range: [0, maxValue],
      },
      font: {
        family: 'Manrope, sans-serif',
      },
    };

    this.populationTrendChart.data = populationTrendData as Data;
    this.populationTrendChart.layout = populationTrendLayout as Layout;
  }

  private fillAgeSexPyramidData(
    ageSex: PopulationSamples,
    naturalId: NaturalId
  ) {
    ageSex
      .byYears()
      .asArray()
      .forEach(([year, data]) => {
        const bothSex = data.populations().reduce((acc, cur) => acc + cur);

        const malePopulationData = data
          .filterSex(Sex.MALE)
          .sumInsideAgeRanges(PopulationService.ageRangesPyramidAggregation)
          .sortedByAge();

        const xMale = malePopulationData.populations();

        const malePercentages = malePopulationData
          .populations()
          .map((pop) => (pop / bothSex) * 100);

        const onlyFem = data
          .filterSex(Sex.FEMALE)
          .sumInsideAgeRanges(PopulationService.ageRangesPyramidAggregation)
          .sortedByAge();

        const femPopulation = onlyFem.populations();
        //make female values negative so that it will be shown beside male values
        const xFemale = femPopulation.map((x) => -x);
        const femalePercentages = femPopulation.map(
          (pop) => (pop / bothSex) * 100
        );

        if (
          year == 0 ||
          ![...xFemale, ...xMale].some((el) => Math.abs(el) >= 10)
        ) {
          this.featureStatus.featureId = naturalId;
          this.featureStatus.containsAgeSexInfo = false;
          return;
        } else {
          this.featureStatus.featureId = naturalId;
          this.featureStatus.containsAgeSexInfo = true;
        }

        //values to be shown on Y axis left from chart
        const y = onlyFem.uniqueAgeRangesStrings();
        this.ageSexAccessibleYears.push(year);

        const maleData = {
          y,
          x: xMale,
          type: 'bar',
          name: 'Male',
          orientation: 'h',
          customdata: xMale.map(
            (population, i) =>
              `${malePercentages[i].toFixed(1)}% (${formatChartRangesAndHoverNumber(population)})`
          ), // append percentages to values
          hovertemplate: `%{customdata}<extra></extra>`,
          marker: { color: MALE_COLOR },
        };

        const femaleData = {
          y,
          x: xFemale,
          type: 'bar',
          name: 'Female',
          orientation: 'h',
          customdata: femPopulation.map(
            (population, i) =>
              `${femalePercentages[i].toFixed(1)}% (${formatChartRangesAndHoverNumber(population)})`
          ),
          hovertemplate: `%{customdata}<extra></extra>`,
          marker: { color: FEMALE_COLOR },
        };

        const layout = {
          autosize: true,
          margin: {
            t: 30,
            r: 35,
          },
          xaxis: this.getXAxisData(xFemale, xMale),
          yaxis: {
            title: 'Age',
            //adjust gaps for y-axis keys
            dtick: 0.1,
          },
          legend: this.layoutLegend,
          barmode: 'overlay',
          bargap: 0.1,
          font: {
            family: 'Manrope, sans-serif',
          },
        } as Layout;

        this.ageSexChartDataByYear[year] = {
          data: [femaleData, maleData] as Data,
          layout,
        };
      });

    this.featureStatus.ageSexYear =
      this.ageSexAccessibleYears.sort()[this.ageSexAccessibleYears.length - 1];
  }

  private getXAxisData(females: number[], males: number[]): XAxisData {
    const totalFemale = females.reduce((sum, x) => sum + Math.abs(x), 0);
    const totalMale = males.reduce((sum, x) => sum + x, 0);
    const totalPop = totalMale + totalFemale;

    const maxPopulation: number = Math.max(...females.map(Math.abs), ...males);
    let maxPopulationCeiled: number;
    if (maxPopulation >= 3000) {
      maxPopulationCeiled = Math.ceil(maxPopulation / 500) * 500;
    } else if (maxPopulation >= 1500) {
      maxPopulationCeiled = Math.ceil(maxPopulation / 250) * 250;
    } else if (maxPopulation >= 750) {
      maxPopulationCeiled = Math.ceil(maxPopulation / 125) * 125;
    } else if (maxPopulation >= 375) {
      maxPopulationCeiled = Math.ceil(maxPopulation / 65) * 65;
    } else {
      maxPopulationCeiled = Math.ceil(maxPopulation / 20) * 20;
    }

    const range: number[] = [
      maxPopulationCeiled * -1.05,
      maxPopulationCeiled * 1.05,
    ];

    const tickvals: number[] = [
      maxPopulationCeiled,
      Math.round(maxPopulationCeiled * 0.65),
      Math.round(maxPopulationCeiled * 0.3),
      0,
      -Math.round(maxPopulationCeiled * 0.3),
      -Math.round(maxPopulationCeiled * 0.65),
      -maxPopulationCeiled,
    ];

    const ticktext: (string | number)[] = tickvals.map((el) => {
      const pct = (Math.abs(el) / totalPop) * 100;
      return `${Math.round(pct)}%`;
    });

    return {
      title: 'Population',
      range: range,
      tickvals: tickvals,
      ticktext: ticktext,
      tickangle: 0,
    };
  }
}
